import PATH from '@/config/PATH';
import { IS_UPDATE_VERSION_3_PROJECT, IS_UPDATE_VERSION_3_PROJECT_LOGIN } from '@/helper/const';
import { formatDataNumberToen } from '@/utils/helper';
import { useAppNavigate } from '@/utils/hooks/useAppNavigate';
import crownIcon from "@assets/web-v3-images//icons/crown-coin-icon.png";
import giftCloseIcon from "@assets/web-v3-images//icons/gift-close-icon.png";
import giftOpenIcon from "@assets/web-v3-images//icons/gift-open-icon.png";
import walletBg from "@assets/web-v3-images//loyalty-point-wallet-section-bg.png";
import trackBg from "@assets/web-v3-images//process-track-bg.png";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router';

// Thời gian giữ class animation (khớp với keyframes pop/bump trong index.css)
const ANIM_MS = 800;

/**
 * Thumb vẽ bằng CSS thay cho process-thumb.png. Ảnh bị kéo giãn theo % nên hai
 * đầu bo tròn méo thành hình elip khi thanh chạy; `rounded-full` + gradient dọc
 * cho ra đúng hình ở mọi bề rộng. Các màu lấy mẫu từ chính file PNG gốc:
 * dọc là dải cam → vệt gloss vàng ở ~31% → đỏ ở đáy; hai đầu tối lại một chút.
 * Vệt tối hai đầu dùng đơn vị px nên không co giãn theo bề rộng.
 */
const THUMB_BODY =
  "linear-gradient(180deg,#FC6E30 0%,#FEAA63 13%,#FFE597 31%,#FEB350 47%,#FD8B39 63%,#FA6221 79%,#F82706 100%)";
const THUMB_EDGES =
  "linear-gradient(90deg,#F92605 0,rgba(249,38,5,0) 10px,rgba(249,38,5,0) calc(100% - 10px),#F92605 100%)";

/**
 * Ví điểm thưởng (Loyalty Point Wallet): thanh tiến trình với các mốc quà.
 * `currentLp` là điểm hiện tại; `milestones` là các mốc nhận quà (mốc cuối = max).
 *
 * Mọi thứ đặt absolute theo % của section (design 816x284) nên khớp ảnh nền ở
 * mọi cỡ màn. Kích thước icon dùng cqw = % bề ngang section:
 * crown 88/816, gift-close 117.75/816, gift-open 139.52/816.
 *
 * Track PNG 761x75 có viền glow: vùng thanh bên trong (chỗ thumb chạy) nằm ở
 * left 2.37%, top 18.67%, w 95.27%, h 50.67% của ảnh track.
 */
const LoyaltyWallet = ({ currentLp = 700, milestones = [500, 1000, 2000], today_withdraw_amount }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navigateApp = useAppNavigate();

  // Các mốc chia đều trên thanh (không theo tỉ lệ giá trị) cho cân đối. Mốc thứ
  // i nằm ở (i+1)/n. Phần đã đạt nội suy tuyến tính trong từng đoạn để thanh
  // luôn dừng đúng vị trí mốc tương ứng với currentLp.
  const step = 100 / milestones.length;
  const markerPct = (i) => (i + 1) * step;

  const lpToPct = (lp) => {
    if (lp <= 0) return 0;
    for (let i = 0; i < milestones.length; i++) {
      const lo = i === 0 ? 0 : milestones[i - 1];
      const hi = milestones[i];
      if (lp <= hi) return (i + (lp - lo) / (hi - lo)) * step;
    }
    return 100;
  };

  const fillPct = lpToPct(currentLp);

  // Hàng nhãn LP rộng đúng bằng vùng trong của track (93.26% * 95.27%)
  const markers = [
    { key: "current", lp: currentLp, leftPct: 0 },
    ...milestones.map((lp, i) => ({ key: lp, lp, leftPct: markerPct(i) })),
  ];

  // Khi điểm tăng: crown nảy, mốc nào vừa vượt qua thì hộp quà bật lên.
  // Dùng key dạng chuỗi để effect không chạy lại vì mảng đổi identity mỗi render.
  const milestonesKey = milestones.join(",");
  const prevLpRef = useRef(currentLp);
  const [justReached, setJustReached] = useState([]);
  const [bumped, setBumped] = useState(false);

  useEffect(() => {
    const prev = prevLpRef.current;
    prevLpRef.current = currentLp;
    if (currentLp <= prev) return;

    setBumped(true);
    setJustReached(
      milestonesKey
        .split(",")
        .map(Number)
        .filter((lp) => prev < lp && currentLp >= lp),
    );

    const id = setTimeout(() => {
      setBumped(false);
      setJustReached([]);
    }, ANIM_MS);
    return () => clearTimeout(id);
  }, [currentLp, milestonesKey]);

  return (
    <section
      onClick={() => IS_UPDATE_VERSION_3_PROJECT_LOGIN ?
        navigateApp(PATH.LOYALTY_POINT) : navigate(PATH.LOYALTY_POINT)}
      style={{ backgroundImage: `url(${walletBg})` }}
      className="@container relative sm:mt-6 -mx-3 aspect-816/284 bg-size-[100%_100%] bg-no-repeat sm:mx-0 intro-tour-home__step-11 cursor-pointer"
    >
      {/* Tiêu đề nằm trong pill có sẵn của ảnh nền (tâm pill ≈ 13.5% chiều cao) */}
      <h2 className="absolute left-1/2 top-[13.5%] -translate-x-1/2 -translate-y-1/2 whitespace-nowrap text-[clamp(12px,3.3cqw,28px)] font-extrabold text-[#8a3213]">
        {t("home.wallet.title")}
      </h2>

      {/* px-[10%] chừa chỗ cho crown (mốc 0%) và hộp quà cuối (mốc 100%) — hai
          marker này lồi ra nửa bề ngang ở hai đầu track, nếu không sẽ tràn panel. */}
      <div className="absolute inset-x-0 top-[58%] -translate-y-1/2 px-[10%]">
        <div
          style={{ backgroundImage: `url(${trackBg})` }}
          className="relative aspect-761/75 w-full bg-size-[100%_100%] bg-no-repeat"
        >
          <div className="absolute left-[2.37%] top-[18.67%] h-[50.67%] w-[95.27%]">
            <div
              style={{
                width: `${fillPct}%`,
                backgroundImage: `${THUMB_EDGES},${THUMB_BODY}`,
              }}
              className="h-full rounded-full transition-[width] duration-700 ease-out motion-reduce:transition-none"
            />

            {/* Vương miện ở đầu thanh */}
            <img
              src={crownIcon}
              alt=""
              className={`cursor-pointer absolute left-0 top-1/2 w-[10.78cqw] -translate-x-1/2 -translate-y-1/2 ${bumped ? "animate-bump motion-reduce:animate-none" : ""
                }`}
            />

            {/* Mốc quà: đã đạt thì hộp mở, chưa đạt thì hộp đóng */}
            {milestones.map((lp, i) => {
              const reached = currentLp >= lp && today_withdraw_amount === lp;
              return (
                <img
                  onClick={() => IS_UPDATE_VERSION_3_PROJECT_LOGIN ?
                    navigateApp(PATH.LOYALTY_POINT) : navigate(PATH.LOYALTY_POINT)}
                  key={lp}
                  src={reached ? giftOpenIcon : giftCloseIcon}
                  alt=""
                  style={{ left: `${markerPct(i)}%` }}
                  className={`btn-click-active absolute top-1/2 -translate-x-1/2 ${reached
                    ? "w-[17.1cqw] translate-y-[-60%]" // nhích lên: nắp hộp mở nằm ở phần trên ảnh
                    : "w-[14.43cqw] -translate-y-1/2"
                    } ${justReached.includes(lp)
                      ? "animate-pop motion-reduce:animate-none"
                      : ""
                    }`}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Nhãn LP: cùng baseline, canh theo tâm từng marker.
          Bề ngang = vùng trong của track = 80% (sau padding) * 95.27% */}
      <div className="absolute left-1/2 top-[73%] w-[76.22%] -translate-x-1/2">
        {markers.map((m) => (
          <p
            key={m.key}
            style={{ left: `${m.leftPct}%` }}
            className="absolute -translate-x-1/2 whitespace-nowrap text-[clamp(9px,2.63cqw,22px)] font-extrabold text-[#8a3213]"
          >
            {t("home.wallet.lp", { value: formatDataNumberToen(m.lp) })}
          </p>
        ))}
      </div>
    </section>
  );
};

export default LoyaltyWallet;
